/**
 * BTR Health — first-party analytics.
 * No third-party trackers. Events POST to /api/analytics/track and live in MongoDB.
 *
 * Usage:
 *   import { track, identify } from "../utils/analytics";
 *   track("click", { id: "hero-search-submit" });
 *   identify(user.user_id);
 *
 *  <RouteTracker /> in App.js auto-fires "page_view" on every route change.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "btr_session_id";
const UTM_KEY = "btr_utm";

function makeId() {
  // Short, unguessable, no need to be cryptographic.
  return (
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 10)
  );
}

function getSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = makeId();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (_) {
    // Private-mode browser: fall back to in-memory id for this tab.
    if (!window.__btr_mem_session) window.__btr_mem_session = makeId();
    return window.__btr_mem_session;
  }
}

function readUtm() {
  // Cache the original UTM params from the first landing so they attach to every event.
  try {
    const cached = localStorage.getItem(UTM_KEY);
    if (cached) {
      const obj = JSON.parse(cached);
      if (obj && (obj.utm_source || obj.utm_medium || obj.utm_campaign)) return obj;
    }
  } catch (_) { /* ignore */ }
  try {
    const sp = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: sp.get("utm_source") || null,
      utm_medium: sp.get("utm_medium") || null,
      utm_campaign: sp.get("utm_campaign") || null,
    };
    if (utm.utm_source || utm.utm_medium || utm.utm_campaign) {
      try { localStorage.setItem(UTM_KEY, JSON.stringify(utm)); } catch (_) { /* ignore */ }
      return utm;
    }
  } catch (_) { /* ignore */ }
  return { utm_source: null, utm_medium: null, utm_campaign: null };
}

let _userId = null;
export function identify(userId) {
  _userId = userId || null;
}

export function track(event, props = {}) {
  if (!event) return;
  const payload = {
    event: String(event).slice(0, 80),
    session_id: getSessionId(),
    path: typeof window !== "undefined" ? window.location.pathname : null,
    referrer: typeof document !== "undefined" ? (document.referrer || null) : null,
    ...readUtm(),
    user_id: _userId,
    props: props || null,
  };

  // Fire-and-forget; never block UI.
  try {
    // sendBeacon survives even on page-unload / tab close.
    const url = `${API}/analytics/track`;
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
    // Fallback to fetch keepalive.
    fetch(url, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => { /* swallow */ });
  } catch (_) { /* ignore */ }
}

/** Hook component — drops in App.js to auto-fire page_view on route changes. */
export function useTrackPageViews() {
  // Lazy require so utilities that import this file from non-React contexts don't break.
  /* eslint-disable react-hooks/rules-of-hooks */
  const { useEffect } = require("react");
  const { useLocation } = require("react-router-dom");
  const location = useLocation();
  useEffect(() => {
    track("page_view", { path: location.pathname, search: location.search || null });
  }, [location.pathname, location.search]);
  /* eslint-enable react-hooks/rules-of-hooks */
}
